import React, { useState } from 'react';
import {
    Alert, Button, Dialog, DialogActions, DialogContent, DialogTitle, TextField,
    Typography, Box, Chip, Accordion, AccordionSummary, AccordionDetails,
    Stepper, Step, StepLabel, Link
} from "@mui/material";
import {
    CloudUpload as UploadIcon,
    ExpandMore as ExpandMoreIcon,
    Key as KeyIcon,
    Info as InfoIcon,
    CheckCircle as SuccessIcon
} from "@mui/icons-material";

const WikidataUploadDialog = ({
    open,
    accessToken,
    uploadProgress,
    savedPlaces,
    errorMessage,
    successMessage,
    onClose,
    onUpload,
    onAccessTokenChange
}) => {
    const [expandedInfo, setExpandedInfo] = useState(false);
    const [expandedSteps, setExpandedSteps] = useState(false);
    const [expandedCors, setExpandedCors] = useState(false);

    const isProduction = process.env.NODE_ENV === 'production' || window.location.hostname !== 'localhost';
    const hasItems = savedPlaces && savedPlaces.size > 0;
    const isComplete = savedPlaces && savedPlaces.size === 0;

    const apiSteps = [
        'Create/Login to your Wikimedia account',
        'Go to Special:AppManagement (OAuth consumer registration)',
        'Click "Create key" for a new OAuth 2.0 application',
        'Fill application details and select "Personal access token"',
        'Copy the generated access token and use it below'
    ];

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="md"
            fullWidth
            PaperProps={{
                sx: {
                    borderRadius: 3,
                    background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
                }
            }}
        >
            <DialogTitle sx={{ pb: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <UploadIcon sx={{ color: 'primary.main' }} />
                    <Typography variant="h5" component="div" sx={{ fontWeight: 600 }}>
                        Upload to Wikidata
                    </Typography>
                    <Chip
                        label={hasItems ? `${savedPlaces.size} items` : 'Complete'}
                        size="small"
                        color={isComplete ? 'success' : 'primary'}
                        icon={isComplete ? <SuccessIcon /> : undefined}
                        sx={{ ml: 'auto' }}
                    />
                </Box>
                <Typography variant="subtitle2" color="text.secondary">
                    Contribute your location names to the world's knowledge base
                </Typography>
            </DialogTitle>
            
            <DialogContent sx={{ pt: 2 }}>
                {/* Production Warning */}
                {isProduction && (
                    <Alert 
                        severity="warning" 
                        sx={{ mb: 3, borderRadius: 2 }}
                        icon={<InfoIcon />}
                        action={
                            <Button 
                                color="inherit" 
                                size="small"
                                onClick={() => setExpandedCors(!expandedCors)}
                            >
                                {expandedCors ? 'Hide' : 'Solutions'}
                            </Button>
                        }
                    >
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            Production Limitation: OAuth uploads blocked by CORS
                        </Typography>
                        {expandedCors && (
                            <Box sx={{ mt: 2, pt: 2, borderTop: '1px solid rgba(255, 152, 0, 0.2)' }}>
                                <Typography variant="body2" sx={{ mb: 1 }}>
                                    <strong>Why this happens:</strong>
                                </Typography>
                                <Typography variant="body2" component="div" sx={{ ml: 2 }}>
                                    • Public CORS proxies block OAuth Authorization headers for security<br/>
                                    • Wikidata requires authenticated API calls for editing<br/>
                                    • Browser security prevents direct cross-origin OAuth requests<br/>
                                    • <strong>Solution:</strong> Run locally with <code>git clone</code> and <code>npm start</code>
                                </Typography>
                            </Box>
                        )}
                    </Alert>
                )}

                {/* Quick Info */}
                <Accordion 
                    expanded={expandedInfo} 
                    onChange={() => setExpandedInfo(!expandedInfo)}
                    sx={{ mb: 2, borderRadius: 2, '&:before': { display: 'none' } }}
                >
                    <AccordionSummary 
                        expandIcon={<ExpandMoreIcon />}
                        sx={{ 
                            backgroundColor: 'rgba(25, 118, 210, 0.04)',
                            borderRadius: 2,
                            '&.Mui-expanded': { borderBottomLeftRadius: 0, borderBottomRightRadius: 0 }
                        }}
                    >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <InfoIcon sx={{ color: 'info.main' }} />
                            <Typography sx={{ fontWeight: 500 }}>
                                What is Wikidata upload?
                            </Typography>
                        </Box>
                    </AccordionSummary>
                    <AccordionDetails sx={{ backgroundColor: 'rgba(25, 118, 210, 0.02)' }}>
                        <Typography variant="body2" sx={{ lineHeight: 1.6 }}>
                            Wikidata is the world's largest free knowledge base. By uploading your location names, 
                            you're contributing valuable linguistic data that helps preserve local knowledge and 
                            makes places more discoverable globally. Your endonyms will be linked to geographic 
                            entities and become part of the public knowledge commons.
                        </Typography>
                    </AccordionDetails>
                </Accordion>

                {/* API Token Steps */}
                <Accordion 
                    expanded={expandedSteps} 
                    onChange={() => setExpandedSteps(!expandedSteps)}
                    sx={{ mb: 3, borderRadius: 2, '&:before': { display: 'none' } }}
                >
                    <AccordionSummary 
                        expandIcon={<ExpandMoreIcon />}
                        sx={{ 
                            backgroundColor: 'rgba(76, 175, 80, 0.04)',
                            borderRadius: 2,
                            '&.Mui-expanded': { borderBottomLeftRadius: 0, borderBottomRightRadius: 0 }
                        }}
                    >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <KeyIcon sx={{ color: 'success.main' }} />
                            <Typography sx={{ fontWeight: 500 }}>
                                How to get your OAuth 2.0 Access Token
                            </Typography>
                        </Box>
                    </AccordionSummary>
                    <AccordionDetails sx={{ backgroundColor: 'rgba(76, 175, 80, 0.02)' }}>
                        <Alert severity="info" sx={{ mb: 2, backgroundColor: 'rgba(25, 118, 210, 0.1)' }}>
                            <Typography variant="body2" sx={{ fontWeight: 500, mb: 0.5 }}>
                                🔒 Why OAuth 2.0 is recommended:
                            </Typography>
                            <Typography variant="body2" sx={{ fontSize: '0.85rem' }}>
                                OAuth 2.0 personal access tokens are more secure than bot passwords and are the 
                                preferred method for API authentication with Wikimedia services.
                            </Typography>
                        </Alert>
                        
                        <Stepper orientation="vertical" sx={{ mt: 1 }}>
                            {apiSteps.map((step, index) => (
                                <Step key={index} active={true}>
                                    <StepLabel>
                                        <Typography variant="body2">{step}</Typography>
                                    </StepLabel>
                                </Step>
                            ))}
                        </Stepper>
                        <Box sx={{ mt: 2, p: 2, backgroundColor: 'rgba(0, 0, 0, 0.03)', borderRadius: 1 }}>
                            <Typography variant="body2" sx={{ fontWeight: 500, mb: 1 }}>
                                Quick Links:
                            </Typography>
                            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                                <Chip
                                    label="Create Wikimedia Account"
                                    component={Link}
                                    href="https://meta.wikimedia.org/wiki/Special:CreateAccount"
                                    target="_blank"
                                    clickable
                                    size="small"
                                    variant="outlined"
                                />
                                <Chip
                                    label="OAuth App Management"
                                    component={Link}
                                    href="https://meta.wikimedia.org/wiki/Special:AppManagement"
                                    target="_blank"
                                    clickable
                                    size="small"
                                    variant="outlined"
                                />
                            </Box>
                        </Box>
                    </AccordionDetails>
                </Accordion>

                {/* Upload Fields */}
                <Box sx={{ space: 2 }}>
                    <TextField
                        label="Upload Progress"
                        value={isComplete ? "All items uploaded ✓" : `${uploadProgress}/${savedPlaces?.size || 0} items`}
                        fullWidth
                        margin="normal"
                        disabled
                        InputProps={{
                            startAdornment: isComplete ? <SuccessIcon sx={{ color: 'success.main', mr: 1 }} /> : undefined
                        }}
                        sx={{
                            '& .MuiInputBase-root': {
                                backgroundColor: isComplete ? 'rgba(76, 175, 80, 0.08)' : undefined
                            }
                        }}
                    />
                    
                    <TextField
                        label="OAuth 2.0 Access Token"
                        value={accessToken}
                        onChange={onAccessTokenChange}
                        fullWidth
                        margin="normal"
                        placeholder="Enter your personal access token from OAuth App Management"
                        helperText="Enter your OAuth 2.0 personal access token (more secure than bot passwords)"
                        type="password"
                        InputProps={{
                            startAdornment: <KeyIcon sx={{ color: 'text.secondary', mr: 1 }} />
                        }}
                    />
                </Box>

                {/* Upload Limit Info */}
                <Alert severity="info" sx={{ mt: 2, borderRadius: 2 }}>
                    <Typography variant="body2">
                        <strong>Upload limits:</strong> Maximum 10 items per batch • 1.5 second delay between uploads • 
                        Successfully uploaded items will be automatically removed from your list
                    </Typography>
                </Alert>

                {/* Messages */}
                {errorMessage && (
                    <Alert severity="error" sx={{ mt: 2, borderRadius: 2 }}>
                        {errorMessage}
                    </Alert>
                )}
                {successMessage && (
                    <Alert severity="success" sx={{ mt: 2, borderRadius: 2 }}>
                        {successMessage}
                    </Alert>
                )}
            </DialogContent>
            
            <DialogActions sx={{ p: 3, gap: 1, backgroundColor: 'rgba(248, 250, 252, 0.5)' }}>
                <Button 
                    onClick={onClose}
                    variant="outlined"
                    sx={{ borderRadius: 2 }}
                >
                    Close
                </Button>
                <Button 
                    onClick={onUpload}
                    variant="contained"
                    disabled={!accessToken.trim() || !hasItems}
                    startIcon={<UploadIcon />}
                    sx={{ borderRadius: 2, px: 3 }}
                >
                    {isComplete ? 'Upload Complete' : `Upload ${savedPlaces?.size || 0} Items`}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default WikidataUploadDialog;